import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Loader2 } from "lucide-react";

/**
 * RequireAuth — gate component for app routes.
 *
 * - user === null  → still loading /me, render spinner
 * - user === false → not authenticated, redirect to /login
 * - user object but email/mobile NOT verified AND OTP still required →
 *   push back to /register so they can resume the OTP flow
 * - otherwise → render children (OTP-off accounts may verify later from profile).
 *   KYC is NOT required here; browse-only access is intentional.
 */
export default function RequireAuth({ children }) {
  const { user, otpVerificationRequired } = useAuth();
  const loc = useLocation();

  if (user === null) {
    return (
      <div
        className="min-h-screen grid place-items-center bg-white text-cm-grey"
        data-testid="auth-loading"
      >
        <Loader2 size={20} className="animate-spin" />
      </div>
    );
  }

  if (user === false) {
    return <Navigate to="/login" replace state={{ from: loc.pathname }} />;
  }

  if (
    otpVerificationRequired &&
    (!user.email_verified || !user.mobile_verified)
  ) {
    return <Navigate to="/register" replace />;
  }

  // OTP optional: still send half-finished signups (no mobile yet) back to register.
  if (
    !otpVerificationRequired &&
    (!user.email_verified || !user.mobile_verified) &&
    !user.mobile_number
  ) {
    return <Navigate to="/register" replace />;
  }

  return children;
}
